import { useEffect, useState } from "react";

import { BokioAssistantSuggestionBlock } from "@bokio/components/BokioAssistant/BokioAssistantSuggestionBlock";
import { ModalWithReceipt } from "@bokio/components/ModalWithReceipt/ModalWithReceipt";
import { Button } from "@bokio/designsystem/components/Button";
import { Notice } from "@bokio/designsystem/components/Notice/Notice";
import { SG } from "@bokio/designsystem/components/SpacingGroup/SpacingGroup";
import { Paragraph, Section } from "@bokio/designsystem/components/TypographicElements/TypographicElements";
import Markdown from "@bokio/elements/Markdown/Markdown";
import { AccountingLangFactory, GeneralLangFactory } from "@bokio/lang";
import { useCompanyInfo } from "@bokio/mobile-web-shared/core/contexts/CompanyInfoContext/CompanyInfoContext";
import * as m from "@bokio/mobile-web-shared/core/model/model";
import { useLazyApi } from "@bokio/mobile-web-shared/hooks/useApi/useApi";
import * as proxy from "@bokio/mobile-web-shared/services/api/proxy";
import { useRouter } from "@bokio/shared/containers/router/useRouter";
import { getRoute } from "@bokio/shared/route";

import { BookkeepingCategoryStep } from "../../scenes/PreBookkeep/components/BookkeepingCategoryStep/BookkeepingCategoryStep";
import { BankRowMatchItem } from "../BankRowMatchItem/BankRowMatchItem";

import type { ValidatedLocalRoute } from "../../utils/localRoute";

import ReceiptCategoryType = m.Core.Contracts.Todo.ReceiptCategoryType;

interface BookkeepingCategoryModalProps {
	visible: boolean;
	onClose: () => void;
	onSuccess: () => void;
	receiptId: string;
	initialCategory?: string;
	/** When true, shows only a Save button without navigation options */
	saveOnly?: boolean;
	/** Called when a bank row is connected to the receipt, passes the AutoVerificationRowId */
	onBankRowConnected?: (autoVerificationRowId: string) => void;
	/** Validated route to return to after bookkeeping. If omitted, no return route is propagated. */
	returnToRoute?: ValidatedLocalRoute;
}

export const BookkeepingCategoryModal = ({
	visible,
	onClose,
	onSuccess,
	receiptId,
	initialCategory,
	saveOnly,
	onBankRowConnected,
	returnToRoute,
}: BookkeepingCategoryModalProps) => {
	const accountingLang = AccountingLangFactory();
	const generalLang = GeneralLangFactory();
	const { companyInfo } = useCompanyInfo();
	const router = useRouter();
	const [category, setCategory] = useState(initialCategory);
	const [bankRowMatch, setBankRowMatch] = useState<m.Bokio.Accounting.Contract.Receipts.DTOs.ReceiptBankRowMatch>();
	const [pendingBankRowId, setPendingBankRowId] = useState<string | undefined>();

	const [getReceipt] = useLazyApi(proxy.Accounting.ReceiptController.GetReceipt.Get, {
		onSuccess: data => setCategory(data.Receipt.SelectedType),
	});

	const [getBankRowMatches, bankRowMatchesRequest] = useLazyApi(proxy.Accounting.ReceiptController.BankRowMatches.Get, {
		onSuccess: data => {
			const matches = data.Matches;
			const perfectMatch = matches.find(m => m.TotalPercentage === 100);
			if (perfectMatch) {
				setBankRowMatch(perfectMatch);
			}
		},
	});

	const [setReceiptType, setReceiptTypeRequest] = useLazyApi(proxy.Accounting.ReceiptController.SetType.Post, {
		onSuccess: () => {
			if (pendingBankRowId && onBankRowConnected) {
				onBankRowConnected(pendingBankRowId);
			}
			onSuccess();
		},
	});

	const [connectReceiptToBankrow, connectReceiptToBankrowRequest] = useLazyApi(
		proxy.Bank.AutoVerificationController.ConnectToReceipt.Post,
		{
			onSuccess: () =>
				setReceiptType(companyInfo.Id, { ReceiptId: receiptId, SelectedType: ReceiptCategoryType.transactionReceipt }),
		},
	);

	const isTransactionReceipt = category === ReceiptCategoryType.transactionReceipt;
	// Block closing when transactionReceipt is selected and there's a bank match suggestion
	const needsBankRowStep = isTransactionReceipt && bankRowMatch !== undefined;

	// Handler for "Categorize and close" - updates receipt type and closes modal
	const handleCategorizeAndClose = () => {
		if (needsBankRowStep) {
			return;
		}
		onSuccess();
	};

	// Handler for "Categorize and create/record" - updates receipt type and redirects to bookkeeping flow
	const handleCategorizeAndContinue = () => {
		if (category) {
			onClose();
			const returnTo = returnToRoute ? encodeURIComponent(returnToRoute) : undefined;

			// Navigate to the bookkeeping flow with the selected category
			if (category === (ReceiptCategoryType.supplier as string)) {
				router.push(
					getRoute(
						"preBookkeepSupplierInvoice",
						{ company: companyInfo.Id, receiptId },
						{ category: "supplier", returnTo },
					),
				);
			} else {
				router.push(getRoute("bookkeepReceipt", { company: companyInfo.Id, category, receiptId }, { returnTo }));
			}
		}
	};

	useEffect(() => {
		if (visible && receiptId) {
			getBankRowMatches(companyInfo.Id, receiptId);
			if (initialCategory) {
				setCategory(initialCategory);
			} else {
				getReceipt(companyInfo.Id, receiptId);
			}
		}
	}, [visible, receiptId, companyInfo.Id, getReceipt, initialCategory, getBankRowMatches]);

	return (
		<ModalWithReceipt
			visible={visible}
			onClose={onClose}
			title={accountingLang.Bookkeeping_CategoryStep_WhatIsYourUploadedMaterial}
			receiptId={receiptId}
		>
			<Section>
				<BokioAssistantSuggestionBlock
					isLoading={bankRowMatchesRequest.isLoading}
					loadingText={generalLang.Loading}
					noBorder
					noPadding
					text={
						bankRowMatch !== undefined ? (
							<Paragraph>{accountingLang.BookkeepingCategoryModal_FoundMatchingBankTransaction}</Paragraph>
						) : (
							<Paragraph testId="NoCategoryPrediction">
								{accountingLang.BookkeepingCategoryModal_NoMatchingBankTransaction}
							</Paragraph>
						)
					}
				>
					{bankRowMatch !== undefined && (
						<SG gap="12">
							<BankRowMatchItem
								date={bankRowMatch.Date}
								text={bankRowMatch.Text}
								amount={bankRowMatch.Amount}
								bookkeepingAccount={bankRowMatch.BookkeepingAccount}
							/>
							<Button
								testId="AcceptCategoryPredictionButton"
								size="small"
								onClick={() => {
									setPendingBankRowId(bankRowMatch.Id);
									connectReceiptToBankrow(companyInfo.Id, {
										ReceiptId: receiptId,
										AutoVerificationRowId: bankRowMatch.Id,
									});
								}}
								loading={connectReceiptToBankrowRequest.isLoading || setReceiptTypeRequest.isLoading}
							>
								{accountingLang.BookkeepingCategoryModal_AttachToTransaction}
							</Button>
						</SG>
					)}
				</BokioAssistantSuggestionBlock>
			</Section>
			<BookkeepingCategoryStep
				category={category}
				onCategoryChange={setCategory}
				receiptId={receiptId}
				onSubmit={handleCategorizeAndContinue}
				onSave={handleCategorizeAndClose}
				onSaveToUploads={handleCategorizeAndClose}
				variant="receipt"
				saveOnly={saveOnly}
				warningNotice={
					isTransactionReceipt && !bankRowMatch ? (
						<Notice
							margin={["bottom"]}
							color="warning"
							testId="BookkeepingCategoryModal_NoBankRowSelectedWarning"
							initialExpand={true}
							collapsible={false}
							title={accountingLang.BookkeepingCategoryModal_NoBankRowSelected_Title}
						>
							<Markdown markdownContent={accountingLang.BookkeepingCategoryModal_NoBankRowSelected_Body} />
						</Notice>
					) : undefined
				}
			/>
		</ModalWithReceipt>
	);
};
